import { faImages, faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons';
import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useApi from '@propertypal/shared/src/hooks/useApi';
import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import useMortgageCalculator from '@propertypal/shared/src/hooks/useMortgageCalculator';
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import { selectUser } from '@propertypal/shared/src/reducers/user';
import trackEvent from '@propertypal/shared/src/services/analytics';
import { BLACK } from '@propertypal/shared/src/styles/theme';
import { Development } from '@propertypal/shared/src/types/development';
import { KeyInfo, Property } from '@propertypal/shared/src/types/property';
import { SoldPriceData } from '@propertypal/shared/src/types/soldPriceData';
import { getGalleryImages } from '@propertypal/shared/src/utils/property';
import { GalleryItem } from '@propertypal/shared/src/utils/property/getGalleryImages';
import { getCategoryAdName } from '@propertypal/shared/src/utils/search/category';
import Accordion, { AccordionHeader } from '@propertypal/web-ui/src/animation/Accordion';
import ListButtons from '@propertypal/web-ui/src/buttons/ListButtons';
import ReportProblemButton from '@propertypal/web-ui/src/buttons/ReportProblemButton';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import ImageModal from '@propertypal/web-ui/src/modal/ImageModal';
import AdFeature from '@propertypal/web-ui/src/property/AdFeature';
import AgentAccreditations from '@propertypal/web-ui/src/property/AgentAccreditations';
import ContactTable from '@propertypal/web-ui/src/property/ContactTable';
import DevelopmentHeader from '@propertypal/web-ui/src/property/DevelopmentHeader';
import KeyInfoBox from '@propertypal/web-ui/src/property/KeyInfoBox';
import OpenViewingOverlay from '@propertypal/web-ui/src/property/OpenViewingOverlay';
import OtherDevelopments from '@propertypal/web-ui/src/property/OtherDevelopments';
import PropertyBanner from '@propertypal/web-ui/src/property/PropertyBanner';
import PropertyDescription from '@propertypal/web-ui/src/property/PropertyDescription';
import PropertyFavourite from '@propertypal/web-ui/src/property/PropertyFavourite';
import PropertySlider from '@propertypal/web-ui/src/property/PropertySlider';
import PropertySummary from '@propertypal/web-ui/src/property/PropertySummary';
import RelatedLinks from '@propertypal/web-ui/src/property/RelatedLinks';
import SimilarProperties from '@propertypal/web-ui/src/property/SimilarProperties';
import Breadcrumbs from '@propertypal/web-ui/src/search/Breadcrumbs';
import { Bold, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { NextRouter, useRouter } from 'next/router';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import IMAGE_LOCATOR from '../../constants/imageLocator';
import favouriteProperty from '../../utils/favouriteProperty';
import useCallAgent from '../../utils/useCallAgent';
import AdBox from '../ads/AdBox';
import MetaTags from '../head/MetaTags';
import GalleryMapButtons from './PropertyDetails.style';
import PropertyPdfBar from './PropertyPdfBar';
import PropertySocialBar from './PropertySocialBar';
import PropertyUnits from './PropertyUnits';

interface Props {
  property: Property;
}

const AreaPriceTracker = dynamic(() => import('@propertypal/web-ui/src/property/AreaPriceTracker'));
const MortgageCalculator = dynamic(() => import('@propertypal/web-ui/src/property/MortgageCalculator'));
const BrokerCalculator = dynamic(() => import('@propertypal/web-ui/src/property/BrokerCalculator'));
const PropertyStats = dynamic(() => import('@propertypal/web-ui/src/property/PropertyStats'));
const SchoolsData = dynamic(() => import('../schools/SchoolsData'));
const EnquiryModal = dynamic(() => import('./EnquiryModal'));
const GoogleMap = dynamic(() => import('../map/GoogleMap'));
const PropertyMapModal = dynamic(() => import('./PropertyMapModal'));
const PropertySlideshowModal = dynamic(() => import('./PropertySlideshowModal'));
const ReportProblemModal = dynamic(() => import('./ReportProblemModal'));
const ShareModal = dynamic(() => import('./ShareModal'));
const StampDutyModal = dynamic(() => import('./StampDutyModal'));

export const PROPERTY_IMAGE_LOCATOR = ['hd', '580x425:FILL_CROP'];

const AD_BOX_1_SIZES = [[320, 50]];
const AD_BOX_1_STYLE = { backgroundColor: BLACK, padding: '10px 0' };
const AD_BOX_2_SIZES = [[728, 90]];
const AD_BOX_2_STYLE = { marginBottom: 45 };
const AD_BOX_3_SIZES = [
  [300, 600],
  [336, 280],
  [300, 250],
];

const getPhotoId = (url: string) => {
  const result = /photo-\d+/.exec(url);

  if (result && result[0]) {
    return parseInt(result[0].replace('photo-', ''), 10) - 1;
  }

  return 0;
};

const routerUpdate = (router: NextRouter, nextUrl: string) => {
  if (
    router.asPath.includes('/video') ||
    router.asPath.includes('/tour') ||
    router.asPath.includes('/slideshow') ||
    router.asPath.includes('/gallery')
  ) {
    return router.replace(nextUrl, undefined, {
      shallow: true,
    });
  }

  return router.push(nextUrl, undefined, {
    shallow: true,
  });
};

const getListButtonLinks = (getReplaceUrl: (url: string) => string, totalImages?: number) => {
  const links = [
    {
      text: 'View Map',
      shortText: 'Map',
      url: getReplaceUrl('/map'),
      icon: faMapMarkerAlt,
      shallow: true,
    },
  ];

  if (totalImages && totalImages > 0) {
    links.push({
      text: 'View Gallery',
      shortText: 'Gallery',
      url: getReplaceUrl('/gallery'),
      icon: faImages,
      shallow: true,
    });
  }

  return links;
};

const PropertyDetails: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const dispatch = useAppDispatch();
  // ignore querystrings when getting url parts
  const urlParts = router.asPath.split('?')[0].split('/');
  const [buyingStatus, setBuyingStatus] = useLocalStorage<string>('buyingStatus', '');
  const { callAgent, modal, onRevealText } = useCallAgent();
  const title = `${props.property.displayAddressLine1}, ${props.property.town} - PropertyPal`;
  const mounted = useRef<boolean>(false);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [favourited, setFavourited] = useState<boolean>(props.property.shortlisted || false);
  const [modalImage, setModalImage] = useState<string>('');
  const [imageIndex, setImageIndex] = useState<number>(() => getPhotoId(router.asPath));
  const [enquiryAccountNumber, setEnquiryAccountNumber] = useState('');
  const showMapModal = urlParts.includes('map');
  const showShareModal = urlParts.includes('share');
  const showEnquiryModal = urlParts.includes('enquiry');
  const showReportModal = urlParts.includes('report-a-problem');
  const showStampDutyModal = urlParts.includes('stamp-duty');
  const showSlideshowModal = !!urlParts.find((part) => /^(slideshow|gallery|video|tour\d+)$/.test(part));
  const showSchoolData =
    props.property.type === 'property' &&
    props.property.propertyType.key === 'residential' &&
    props.property.price?.priceType !== 'PUBLIC_AUCTIONS' &&
    props.property.published;
  const images = getGalleryImages(props.property, PROPERTY_IMAGE_LOCATOR[0], true, true);
  const adName = getCategoryAdName(props.property.propertyType.key, props.property.saleType.key);
  const adSuffix = `property_propertydetails_${adName}`;
  const [soldPriceData, soldPriceError, soldPriceLoading, fetchSoldPriceData] = useApi<SoldPriceData>(
    `/area-price-tracker/property/${props.property.id}`,
    false,
  );
  const similarUrl = `/property/${props.property.id}/${
    props.property.development ? 'other-types' : 'similar'
  }?pageSize=3&imageLocator=${IMAGE_LOCATOR}`;
  const [similarProperties, , , fetchSimilarProperties] = useApi<Property[]>(similarUrl, false);
  const [otherDevelopments, , , fetchOtherDevelopments] = useApi<Development[]>(
    `/development/${props.property.id}/other-developments?imageLocator=${IMAGE_LOCATOR}`,
    false,
  );
  const user = useSelector(selectUser);
  const mortgageCalculator = useMortgageCalculator(props.property);

  const getReplaceUrl = (nextSection: string) => {
    // get current url without querystrings
    const currentUrl = router.asPath.split('?')[0];
    const index = currentUrl.search(/photo-\d+/);
    const photo = index > -1 ? `/${router.asPath.substring(index)}` : '';
    // get any querystrings
    const qs = router.asPath.split('?')[1];

    return `${props.property.path}${nextSection}${photo}${qs ? `?${qs}` : ''}`;
  };

  const toggleFavourited = async () => {
    const loggedIn = await favouriteProperty(dispatch)(!favourited, props.property.id);

    if (loggedIn) setFavourited(!favourited);

    return loggedIn;
  };

  const updateSectionUrl = (nextSection: string) => {
    const nextUrl = getReplaceUrl(nextSection);

    routerUpdate(router, nextUrl);
  };

  const handleBack = () => {
    if (window.history.length <= 2) {
      updateSectionUrl('');
    } else {
      router.back();
    }
  };

  const updatePhotoUrl = (index: number) => {
    const url = router.asPath.replace(/\/photo-\d+/, '');
    // get current url without querystrings
    const currentUrl = url.split('?')[0];
    // get any querystrings
    const qs = url.split('?')[1];
    const photo = index > 0 ? `/photo-${index + 1}` : '';

    router.replace(`${currentUrl}${photo}${qs ? `?${qs}` : ''}`, undefined, {
      shallow: true,
    });
  };

  const openSlideshow = async (index: number) => {
    if (imageIndex === index) {
      updateSectionUrl('/slideshow');
    } else {
      const nextUrl = getReplaceUrl('/slideshow');
      await routerUpdate(router, nextUrl);
      // updating index will trigger updatePhotoUrl
      setImageIndex(index);
    }
  };

  const handleImageClick = (image: GalleryItem) => {
    if (image.type === 'webview' && image.insecure) {
      window.open(image.link, image.text, 'popup');
    } else if (image.type === 'webview') {
      updateSectionUrl(`/tour${image.tourIndex}`);
    } else if (image.type === 'video') {
      updateSectionUrl('/video');
    } else {
      trackEvent('Detail Page', 'Mediabox', 'Photo');
      updateSectionUrl('/slideshow');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          url: props.property.shareURL,
          text: `Found this on PropertyPal`,
        });
      } catch {
        // hide native share errors. Commonly happens if share button is pressed twice in quick succession
      }
    } else {
      updateSectionUrl('/share');
    }
  };

  const handleInfoClick = (keyInfo: KeyInfo) => {
    if (keyInfo.image) {
      setModalImage(keyInfo.image);
    }

    if (keyInfo.key === 'EPC_FILES' && keyInfo.link) {
      window.open(keyInfo.link);
    }

    if (keyInfo.key === 'STAMP_DUTY_COMPLEX') {
      router.push(`${props.property.path}/stamp-duty`, undefined, { shallow: true });
    }

    if (keyInfo.key === 'TYPICAL_MORTGAGE') {
      if (isMobile && !openSections.includes('mortgageCalculator')) {
        setOpenSections([...openSections, 'mortgageCalculator']);
      }

      const mortgageCalc = document.getElementById('calculator-marker');
      mortgageCalc?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderAccordionHeader = (key: string, text: string) => {
    if (!isMobile) return undefined;

    const toggleOpen = () => {
      if (openSections.includes(key)) {
        const updatedSections = openSections.filter((section) => section !== key);
        setOpenSections(updatedSections);
      } else {
        setOpenSections([...openSections, key]);
      }
    };
    return <AccordionHeader active={openSections.includes(key)} text={text} onClick={toggleOpen} testID={key} />;
  };

  const listButtonLinks = getListButtonLinks(getReplaceUrl, props.property.images?.length);

  const onFavourite = useCallback(() => {
    return favouriteProperty(dispatch);
  }, []);

  useEffect(() => {
    // Important to prevent router.replace being called immediately.
    // Causes a bug within next.js where getServerSideProps won't work on history.back
    if (mounted.current) {
      updatePhotoUrl(imageIndex);
    }

    mounted.current = true;
  }, [imageIndex]);

  return (
    <>
      <Head>
        <title>{title}</title>

        <MetaTags
          title={title}
          url={props.property.path}
          shortlink={props.property.shareURL}
          type="place"
          image={
            props.property.images?.length
              ? {
                  url: props.property.images[0].url,
                  width: String(props.property.images[0].width),
                  height: String(props.property.images[0].height),
                }
              : undefined
          }
          updatedTime={props.property.listingUpdatedTime}
          latlng={{
            latitude: props.property.coordinate?.latitude,
            longitude: props.property.coordinate?.longitude,
          }}
          accountNumber={props.property.account.accountNumber}
          metaDescriptions={props.property.metaDescriptions}
        />
        <meta name="schools-checker-enabled" content={!showSchoolData ? 'false' : 'true'} />
      </Head>

      {!props.property.featuredDevelopmentContent && (
        <AdBox
          id={`top_${adSuffix}`}
          sizeKey="320x50"
          sizes={AD_BOX_1_SIZES}
          hideOver={780}
          containerStyle={AD_BOX_1_STYLE}
        />
      )}

      <div className="pp-details-page">
        {props.property.featuredDevelopmentContent?.contentTop && (
          <DevelopmentHeader
            html={props.property.featuredDevelopmentContent?.contentTop}
            property={props.property}
            favourited={favourited}
            onFavourite={toggleFavourited}
            onOpenSlideshowClick={() => openSlideshow(0)}
            onImageClick={(index) => openSlideshow(index)}
          />
        )}

        {!props.property.featuredDevelopmentContent?.contentTop &&
          props.property.coordinate &&
          !urlParts.includes('map') &&
          images.length === 0 && (
            <>
              <GoogleMap
                width="100%"
                height={300}
                lat={props.property.coordinate.latitude}
                lng={props.property.coordinate.longitude}
                zoom={props.property.coordinate.mapZoomLevel}
                markers={[
                  {
                    id: 1,
                    lat: props.property.coordinate.latitude,
                    lng: props.property.coordinate.longitude,
                  },
                ]}
              />
            </>
          )}

        {!props.property.featuredDevelopmentContent?.contentTop && images.length > 0 && (
          <PropertySlider
            alt={props.property.addressLine1}
            images={images}
            imageIndex={imageIndex}
            setImageIndex={setImageIndex}
            onClick={handleImageClick}
            onCounterClick={handleImageClick}
            setHeight={(windowWidth, windowHeight, imageHeight) => {
              if (windowWidth <= 650) return Math.min(imageHeight, 400);
              if (windowWidth <= 1024) return Math.min(windowHeight * 0.6, imageHeight);

              return Math.min(windowHeight * 0.75, imageHeight);
            }}
            centerImageCounter
            renderImageOverlay={() => <PropertyBanner property={props.property} disablePremium disableFeatured />}
            enableKeyboardControls={!showSlideshowModal}
            updateOnIndexChange={!showSlideshowModal}
          >
            {props.property.type === 'property' && (
              <PropertyFavourite
                containerStyle={{ position: 'absolute', top: 15, right: 15, zIndex: 10 }}
                favourited={favourited}
                onFavourite={toggleFavourited}
              />
            )}
            {props.property.openViewing && <OpenViewingOverlay openViewing={props.property.openViewing} slider />}
          </PropertySlider>
        )}

        <GalleryMapButtons>
          <ListButtons links={listButtonLinks} />
        </GalleryMapButtons>

        <div className="pp-top-section">
          <Breadcrumbs crumbs={props.property.breadcrumbs} />

          <PageWrapper className="pp-summary-box">
            <PropertySummary
              property={props.property}
              onCallClick={() => callAgent(props.property)}
              onShowNumberClick={() => onRevealText(props.property, props.property.contacts?.default)}
              onEnquiryClick={() => updateSectionUrl('/enquiry')}
            />
            {props.property.keyInfo && (
              <>
                <SubHeading mt={50} mb={30}>
                  Key Information
                </SubHeading>

                <KeyInfoBox
                  property={props.property}
                  onClick={handleInfoClick}
                  buyingStatus={buyingStatus}
                  mortgageCalculator={mortgageCalculator}
                />
              </>
            )}
          </PageWrapper>

          {props.property.properties && props.property.published && (
            <SimilarProperties
              properties={props.property.properties}
              imageLocator={PROPERTY_IMAGE_LOCATOR[1]}
              onFavourite={onFavourite}
            />
          )}

          {!props.property.featuredDevelopmentContent && (
            <AdBox
              id={`top_${adSuffix}`}
              sizeKey="728x90"
              sizes={AD_BOX_2_SIZES}
              hideUnder={779}
              containerStyle={AD_BOX_2_STYLE}
            />
          )}

          {!props.property.featuredDevelopmentContent && (
            <AdBox
              id={`top_${adSuffix}`}
              sizeKey="300x250"
              sizes={AD_BOX_3_SIZES}
              hideOver={780}
              containerStyle={AD_BOX_2_STYLE}
            />
          )}

          {props.property.type === 'property' && (
            <PropertySocialBar
              property={props.property}
              favourited={favourited}
              onFavourite={toggleFavourited}
              onShare={handleShare}
            />
          )}

          <PageWrapper className="pp-description-box">
            <PropertyDescription
              property={props.property}
              onVideoClick={updateSectionUrl}
              onImageClick={openSlideshow}
              images={images}
            />
          </PageWrapper>

          <PropertyPdfBar property={props.property} onVideoClick={updateSectionUrl} />
        </div>

        {!!props.property.account?.accreditations?.length &&
          props.property.type === 'property' &&
          !props.property.featuredDevelopmentContent && (
            <Accordion
              isOpen={!isMobile || openSections.includes('accreditations')}
              header={renderAccordionHeader('accreditations', 'Agent Accreditations')}
            >
              <PageWrapper>
                {!isMobile && <SubHeading mt={60}>Agent Accreditations</SubHeading>}

                <AgentAccreditations accreditations={props.property.account.accreditations} />
              </PageWrapper>
            </Accordion>
          )}

        {mortgageCalculator.hasMortgageCalculator && (
          <>
            <div id="calculator-marker" />

            <Accordion
              isOpen={!isMobile || openSections.includes('mortgageCalculator')}
              header={renderAccordionHeader('mortgageCalculator', 'Mortgage Calculator')}
            >
              <PageWrapper>
                {!isMobile && <SubHeading mt={60}>Mortgage Calculator</SubHeading>}

                {mortgageCalculator.hasBrokerCalculator &&
                  props.property.brokers &&
                  props.property.brokers.map((b) => (
                    <BrokerCalculator
                      property={props.property}
                      broker={b}
                      key={b.id}
                      mortgageCalculator={mortgageCalculator}
                    />
                  ))}

                {!mortgageCalculator.hasBrokerCalculator && (
                  <MortgageCalculator
                    property={props.property}
                    mortgageCalculator={mortgageCalculator}
                    passportId={user.profile?.currentPassportId}
                  />
                )}
              </PageWrapper>
            </Accordion>
          </>
        )}

        {props.property.units && (
          <Accordion
            isOpen={!isMobile || openSections.includes('propertyUnits')}
            header={renderAccordionHeader('propertyUnits', 'Property Units')}
          >
            <PropertyUnits property={props.property} />
          </Accordion>
        )}

        {(props.property.type === 'development' || props.property.development) &&
          props.property.contacts &&
          props.property.contacts.all.length > 1 &&
          props.property.published && (
            <Accordion
              className="pp-contact-box"
              isOpen={!isMobile || openSections.includes('contactTable')}
              header={renderAccordionHeader('contactTable', 'Contact the Agent')}
            >
              <ContactTable
                property={props.property}
                onEnquiryClick={(contact) => {
                  setEnquiryAccountNumber(contact.accountNumber);
                  updateSectionUrl('/enquiry');
                }}
              />
            </Accordion>
          )}

        {showSchoolData && (
          <Accordion
            isOpen={!isMobile || openSections.includes('schoolsData')}
            header={renderAccordionHeader('schoolsData', 'Schools Data')}
          >
            <SchoolsData property={props.property} />
          </Accordion>
        )}

        {props.property.priceTrackerAvailable && props.property.published && (
          <Accordion
            isOpen={!isMobile || openSections.includes('areaPriceTracker')}
            header={renderAccordionHeader('areaPriceTracker', 'Area Price Tracker')}
          >
            <AreaPriceTracker
              soldPriceData={soldPriceData}
              onInView={fetchSoldPriceData}
              loading={soldPriceLoading}
              error={soldPriceError}
            />
          </Accordion>
        )}

        {props.property.stats && props.property.stats.history && props.property.published && (
          <Accordion
            isOpen={!isMobile || openSections.includes('stats')}
            header={renderAccordionHeader('stats', 'Property Statistics')}
          >
            <PropertyStats property={props.property} />
          </Accordion>
        )}

        {props.property.type === 'property' && props.property.published && (
          <SimilarProperties
            properties={similarProperties}
            onInView={fetchSimilarProperties}
            title={props.property.development ? 'More from this development' : 'Similar Properties'}
            background={theme.backgroundLightest}
            hideOnEmpty={!!props.property.development}
            onFavourite={onFavourite}
            emptyText={
              props.property.development
                ? 'No more properties found for this development'
                : "Sorry, we couldn't find any similar properties to this."
            }
          />
        )}

        {props.property.type === 'property' &&
          !props.property.development &&
          props.property.countryCode === 'GBR' &&
          props.property.published && <AdFeature property={props.property} />}

        {props.property.featuredDevelopmentContent && (
          <div
            className="pp-content-middle"
            dangerouslySetInnerHTML={{ __html: props.property.featuredDevelopmentContent.contentMiddle }}
          />
        )}

        {props.property.type === 'development' && (
          <OtherDevelopments data={otherDevelopments} onInView={fetchOtherDevelopments} />
        )}

        {props.property.featuredDevelopmentContent && (
          <div
            className="pp-content-bottom"
            dangerouslySetInnerHTML={{ __html: props.property.featuredDevelopmentContent.contentBottom }}
          />
        )}

        <PageWrapper>
          <Text align="center" mt={30} mb={30} className="pp-report-a-problem">
            <Bold>SPOTTED A PROBLEM?</Bold> Even the best agents make mistakes, help us to maintain accurate property
            information on our listings. <ReportProblemButton onClick={() => updateSectionUrl('/report-a-problem')} />
          </Text>
        </PageWrapper>

        <RelatedLinks property={props.property} />

        <ImageModal
          ariaLabel="EPC image popup"
          show={!!modalImage}
          onClose={() => setModalImage('')}
          imageUrl={modalImage}
        />

        {showStampDutyModal && (
          <StampDutyModal
            buyingStatus={buyingStatus}
            setBuyingStatus={setBuyingStatus}
            property={props.property}
            show={showStampDutyModal}
            onClose={handleBack}
          />
        )}

        {showMapModal && (
          <PropertyMapModal show={showMapModal} property={props.property} onClose={handleBack} adSuffix={adSuffix} />
        )}

        {showSlideshowModal && (
          <PropertySlideshowModal
            imageIndex={imageIndex}
            setImageIndex={setImageIndex}
            show={showSlideshowModal}
            property={props.property}
            onClose={handleBack}
            onTabClick={(tab) => updateSectionUrl(`/${tab}`)}
            url={router.asPath}
            imageLocator={PROPERTY_IMAGE_LOCATOR[0]}
            adSuffix={adSuffix}
          />
        )}

        {showEnquiryModal && (
          <EnquiryModal
            accountNumber={enquiryAccountNumber}
            show={showEnquiryModal}
            onClose={handleBack}
            property={props.property}
          />
        )}

        {showReportModal && (
          <ReportProblemModal show={showReportModal} onClose={handleBack} property={props.property} />
        )}

        {showShareModal && (
          <ShareModal show={router.asPath.includes('/share')} onClose={handleBack} property={props.property} />
        )}

        {modal}

        {props.property.featuredDevelopmentContent && <style>{props.property.featuredDevelopmentContent.css}</style>}
      </div>
    </>
  );
};

export default PropertyDetails;
